/**
 * PlanoUsageBar — barra de progresso de uso do plano CROSS M3.
 *
 * Exclusivo da tela Plano (BillingPage). Prefixo Plano conforme regra de isolamento.
 *
 * Props:
 *   label        — nome do eixo (ex: "Fontes conectadas")
 *   current      — valor atual
 *   max          — valor maximo (0 = ilimitado)
 *   unit         — sufixo do valor (ex: "fontes", "meses")
 *   limitReached — quando true, exibe chip "Limite atingido"
 *   deferred     — quando true, omite a barra e exibe data-gap
 *   deferredNote — texto do chip de data-gap
 *   note         — texto descritivo abaixo da barra (conforme prototipo)
 *
 * Acessibilidade: role="progressbar" com aria-valuenow/min/max + aria-label descritivo.
 * Tokens: usa apenas variaveis de tokens.css — zero hardcode.
 */

interface PlanoUsageBarProps {
  readonly label: string;
  readonly current: number;
  /** 0 = ilimitado */
  readonly max: number;
  readonly unit?: string;
  readonly limitReached?: boolean;
  readonly deferred?: boolean;
  readonly deferredNote?: string;
  /** Nota descritiva abaixo da barra — ex: "Limite do plano atingido." */
  readonly note?: string | undefined;
}

function barFillClass(pct: number, limitReached: boolean): string {
  if (limitReached) return 'plano-usage-bar-fill plano-usage-bar-fill--full';
  if (pct >= 80) return 'plano-usage-bar-fill plano-usage-bar-fill--warn';
  return 'plano-usage-bar-fill plano-usage-bar-fill--ok';
}

export function PlanoUsageBar({
  label,
  current,
  max,
  unit = '',
  limitReached = false,
  deferred = false,
  deferredNote,
  note,
}: PlanoUsageBarProps): JSX.Element {
  const isUnlimited = max === 0;
  const pct = isUnlimited ? 5 : Math.max(0, Math.min((current / max) * 100, 100));
  const countLabel = isUnlimited
    ? `${current} de ilimitados${unit.length > 0 ? ` ${unit}` : ''}`
    : `${current} de ${max}${unit.length > 0 ? ` ${unit}` : ''}`;

  const ariaLabel = `${label}: ${countLabel}${limitReached ? ', limite atingido' : ''}`;

  return (
    <div className="plano-usage-item">
      <div className="plano-usage-item-head">
        <span className="plano-usage-item-label">{label}</span>
        <span className="plano-usage-item-count">{countLabel}</span>
      </div>

      {deferred ? (
        <span className="plano-data-gap-chip">
          <span className="plano-data-gap-dot" aria-hidden="true" />
          {deferredNote ?? 'dado indisponivel'}
        </span>
      ) : (
        <div
          className="plano-usage-bar-track"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={ariaLabel}
        >
          <div className={barFillClass(pct, limitReached)} style={{ width: `${pct}%` }} />
        </div>
      )}

      {limitReached ? (
        <span className="plano-usage-full-chip" role="note">
          <span className="plano-usage-full-icon" aria-hidden="true">
            info
          </span>
          Limite atingido
        </span>
      ) : null}

      {/* Nota descritiva abaixo da barra — conforme prototipo plano.html */}
      {note !== undefined && note.length > 0 ? (
        <p className="plano-usage-item-note">{note}</p>
      ) : null}
    </div>
  );
}
